/**
 * TextureAtlasAllocator - Cell-based texture allocation for WebGL2
 *
 * Manages a TEXTURE_2D_ARRAY as a pool of fixed-size cells.
 * Returns numeric cell indices like malloc() - caller maintains any name mappings.
 *
 * This enables:
 * - On-demand sprite loading
 * - Dynamic sprite replacement for editing
 * - Memory management via cell free/reuse
 */

/** Cell location info for rendering */
export interface CellInfo {
  /** Layer index in the texture array */
  layer: number;
  /** Pixel X offset in texture */
  x: number;
  /** Pixel Y offset in texture */
  y: number;
}

export type ImageSource = HTMLImageElement | ImageData | HTMLCanvasElement | ArrayBufferView;

export class TextureAtlasAllocator {
  private gl: WebGL2RenderingContext;
  private texture: WebGLTexture | null = null;
  private initialized = false;

  // Cell dimensions
  readonly cellWidth: number;
  readonly cellHeight: number;

  // Texture dimensions
  private textureWidth: number;
  private textureHeight: number;
  private layerCount: number;
  private maxLayerCount: number;

  // Cell grid
  private cellsPerRow: number;
  private cellsPerCol: number;
  private cellsPerLayer: number;

  // Allocation tracking - just track which indices are in use
  private allocatedCells = new Set<number>();
  private freeCells: number[] = []; // Recycled cell indices
  private nextCell = 0; // Next unallocated cell index

  /**
   * Create a new allocator.
   * @param gl WebGL2 context
   * @param cellWidth Width of each cell in pixels
   * @param cellHeight Height of each cell in pixels
   * @param textureWidth Width of each texture layer
   * @param textureHeight Height of each texture layer
   * @param initialLayerCount Initial number of layers (will grow as needed)
   * @param maxLayerCount Maximum layers to grow to
   */
  constructor(
    gl: WebGL2RenderingContext,
    cellWidth: number,
    cellHeight: number,
    textureWidth: number = 512,
    textureHeight: number = 512,
    initialLayerCount: number = 4,
    maxLayerCount: number = 256
  ) {
    this.gl = gl;
    this.cellWidth = cellWidth;
    this.cellHeight = cellHeight;
    this.textureWidth = textureWidth;
    this.textureHeight = textureHeight;
    this.layerCount = initialLayerCount;
    this.maxLayerCount = maxLayerCount;

    this.cellsPerRow = Math.floor(textureWidth / cellWidth);
    this.cellsPerCol = Math.floor(textureHeight / cellHeight);
    this.cellsPerLayer = this.cellsPerRow * this.cellsPerCol;
  }

  /**
   * Initialize the texture array storage.
   * Must be called before any allocations.
   */
  initialize(): void {
    if (this.initialized) return;

    this.texture = this.createTextureArray(this.layerCount);
    this.initialized = true;
  }

  /**
   * Create a new texture array with the specified layer count.
   */
  private createTextureArray(layers: number): WebGLTexture {
    const gl = this.gl;
    const texture = gl.createTexture();
    if (!texture) {
      throw new Error('Failed to create WebGL texture');
    }

    gl.bindTexture(gl.TEXTURE_2D_ARRAY, texture);
    gl.texStorage3D(
      gl.TEXTURE_2D_ARRAY,
      1,
      gl.RGBA8,
      this.textureWidth,
      this.textureHeight,
      layers
    );

    // NEAREST filtering for pixel-perfect sprite sampling in pass 1
    // The pixel-art AA is applied in pass 2 when sampling the offscreen texture
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    return texture;
  }

  /**
   * Grow the texture array by doubling its layer count.
   * Copies existing data to the new texture.
   */
  private grow(): void {
    const gl = this.gl;
    const oldLayerCount = this.layerCount;
    const newLayerCount = Math.min(oldLayerCount * 2, this.maxLayerCount);

    if (newLayerCount === oldLayerCount) {
      throw new Error(`Texture atlas at max capacity: ${this.maxLayerCount} layers`);
    }

    // Create new larger texture
    const newTexture = this.createTextureArray(newLayerCount);
    const oldTexture = this.texture!;

    // Create framebuffers for copying
    const readFb = gl.createFramebuffer();
    const drawFb = gl.createFramebuffer();

    // Copy each existing layer
    for (let layer = 0; layer < oldLayerCount; layer++) {
      // Attach old texture layer to read framebuffer
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, readFb);
      gl.framebufferTextureLayer(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, oldTexture, 0, layer);

      // Attach new texture layer to draw framebuffer
      gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, drawFb);
      gl.framebufferTextureLayer(gl.DRAW_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, newTexture, 0, layer);

      // Blit the layer
      gl.blitFramebuffer(
        0,
        0,
        this.textureWidth,
        this.textureHeight,
        0,
        0,
        this.textureWidth,
        this.textureHeight,
        gl.COLOR_BUFFER_BIT,
        gl.NEAREST
      );
    }

    // Clean up
    gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
    gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);
    gl.deleteFramebuffer(readFb);
    gl.deleteFramebuffer(drawFb);
    gl.deleteTexture(oldTexture);

    this.texture = newTexture;
    this.layerCount = newLayerCount;
  }

  /**
   * Convert linear cell index to layer/x/y pixel coordinates.
   */
  getCellInfo(index: number): CellInfo {
    const layer = Math.floor(index / this.cellsPerLayer);
    const withinLayer = index % this.cellsPerLayer;
    const cellY = Math.floor(withinLayer / this.cellsPerRow);
    const cellX = withinLayer % this.cellsPerRow;
    return {
      layer,
      x: cellX * this.cellWidth,
      y: cellY * this.cellHeight,
    };
  }

  /**
   * Allocate a cell.
   * @returns The cell index (like malloc returning a pointer)
   * @throws If atlas is at max capacity and full
   */
  allocate(): number {
    if (!this.initialized) {
      throw new Error('TextureAtlasAllocator not initialized');
    }

    // Get next available cell - prefer recycled cells
    const cellIndex = this.freeCells.pop() ?? this.nextCell++;
    const totalCells = this.cellsPerLayer * this.layerCount;

    // Grow if we've exceeded current capacity
    if (cellIndex >= totalCells) {
      this.grow();
    }

    this.allocatedCells.add(cellIndex);
    return cellIndex;
  }

  /**
   * Upload image data to an allocated cell.
   * @param index Cell index from allocate()
   * @param image Image data (must match cell dimensions). For ArrayBufferView,
   *              must be RGBA with cellWidth * cellHeight * 4 bytes.
   */
  upload(index: number, image: ImageSource): void {
    if (!this.allocatedCells.has(index)) {
      throw new Error(`Cell ${index} not allocated`);
    }

    const info = this.getCellInfo(index);
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, this.texture);

    // ArrayBufferView doesn't have width/height, skip dimension check
    const isArrayBuffer = ArrayBuffer.isView(image);
    if (!isArrayBuffer) {
      const img = image as HTMLImageElement | ImageData | HTMLCanvasElement;
      if (img.width !== this.cellWidth || img.height !== this.cellHeight) {
        throw new Error(
          `Image must be ${this.cellWidth}x${this.cellHeight}, got ${img.width}x${img.height}`
        );
      }
    }

    // FLIP_Y only works with TexImageSource, not ArrayBufferView on 3D textures
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, !isArrayBuffer);
    gl.texSubImage3D(
      gl.TEXTURE_2D_ARRAY,
      0,
      info.x,
      info.y,
      info.layer,
      this.cellWidth,
      this.cellHeight,
      1,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      // Type assertion needed because WebGL has separate overloads
      image as ArrayBufferView
    );
  }

  /**
   * Allocate and upload in one call.
   * @param image Image data (must match cell dimensions)
   * @returns The cell index
   */
  allocateAndUpload(image: ImageSource): number {
    const index = this.allocate();
    this.upload(index, image);
    return index;
  }

  /**
   * Free a cell, allowing it to be reused.
   * @param index Cell index to free
   */
  free(index: number): void {
    if (!this.allocatedCells.has(index)) return;
    this.allocatedCells.delete(index);
    this.freeCells.push(index);
  }

  /**
   * Check if a cell is allocated.
   */
  isAllocated(index: number): boolean {
    return this.allocatedCells.has(index);
  }

  /**
   * Get the WebGL texture for binding.
   */
  getTexture(): WebGLTexture | null {
    return this.texture;
  }

  /**
   * Get texture dimensions.
   */
  getDimensions(): { width: number; height: number } {
    return { width: this.textureWidth, height: this.textureHeight };
  }

  /**
   * Get allocation statistics.
   */
  getStats(): {
    allocated: number;
    free: number;
    total: number;
    maxTotal: number;
    layers: number;
    maxLayers: number;
    utilizationPercent: number;
  } {
    const total = this.cellsPerLayer * this.layerCount;
    const maxTotal = this.cellsPerLayer * this.maxLayerCount;
    const allocated = this.allocatedCells.size;
    return {
      allocated,
      free: total - allocated,
      total,
      maxTotal,
      layers: this.layerCount,
      maxLayers: this.maxLayerCount,
      utilizationPercent: (allocated / total) * 100,
    };
  }

  /**
   * Check if the allocator is initialized.
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Clean up all WebGL resources.
   */
  dispose(): void {
    if (this.texture) {
      this.gl.deleteTexture(this.texture);
      this.texture = null;
    }
    this.allocatedCells.clear();
    this.freeCells = [];
    this.nextCell = 0;
    this.initialized = false;
  }
}
